import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { getSettings } from "@/lib/api";
import { Sprout, Phone, Truck, ShieldCheck, MessageCircle, Mail, Clock, Map, User } from "lucide-react";

const formatPhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return null;
  // Indian format: last 10 digits shown as +91 XXXXX XXXXX
  const ten = digits.length > 10 ? digits.slice(-10) : digits;
  if (ten.length === 10) return `+91 ${ten.slice(0, 5)} ${ten.slice(5)}`;
  return `+${digits}`;
};

const Footer = () => {
  const { t } = useApp();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const phone = formatPhone(settings?.shop_phone || settings?.shop_whatsapp);
  const waNumber = (settings?.shop_whatsapp || "").replace(/\D/g, "");

  // Simple open/closed indicator: default Mon-Sat 8-20 unless business_hours field parsed
  const now = new Date();
  const dow = now.getDay(); // 0=Sunday
  const hour = now.getHours() + now.getMinutes() / 60;
  const isOpen = dow >= 1 && dow <= 6 && hour >= 8 && hour < 20;

  return (
    <footer className="mt-16 border-t border-[#E2E8F0] bg-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-[#2D5A27] flex items-center justify-center">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-[#1E293B]">{t("appName")}</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{t("aboutText")}</p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm mb-3 text-[#1E293B]">{t("products")}</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/products?category=pesticides" className="hover:text-[#2D5A27]">{t("pesticides")}</Link></li>
              <li><Link to="/products?category=fertilizers" className="hover:text-[#2D5A27]">{t("fertilizers")}</Link></li>
              <li><Link to="/products?category=seeds" className="hover:text-[#2D5A27]">{t("seeds")}</Link></li>
              <li><Link to="/products?category=tools" className="hover:text-[#2D5A27]">{t("tools")}</Link></li>
              <li><Link to="/products?category=organic_pesticides" className="hover:text-[#2D5A27]">{t("organic_pesticides")}</Link></li>
              <li><Link to="/products?category=organic_produce" className="hover:text-[#2D5A27]">{t("organic_produce")}</Link></li>
              <li><Link to="/products?category=residue_free" className="hover:text-[#2D5A27]">{t("residue_free")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm mb-3 text-[#1E293B]">{t("contact")}</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              {phone && (
                <li>
                  <a href={`tel:${(settings?.shop_phone || settings?.shop_whatsapp || "").replace(/\D/g, "")}`} className="flex items-center gap-2 hover:text-[#2D5A27]" data-testid="footer-phone">
                    <Phone className="w-4 h-4 text-[#2D5A27]" /> {phone}
                  </a>
                </li>
              )}
              {waNumber && (
                <li>
                  <a
                    href={`https://wa.me/${waNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-emerald-700"
                    data-testid="footer-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
                  </a>
                </li>
              )}
              {settings?.shop_email && (
                <li>
                  <a href={`mailto:${settings.shop_email}`} className="flex items-center gap-2 hover:text-[#D97706] break-all" data-testid="footer-email">
                    <Mail className="w-4 h-4 text-[#D97706] shrink-0" /> {settings.shop_email}
                  </a>
                </li>
              )}
              {settings?.business_hours && (
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#2D5A27] shrink-0 mt-0.5" />
                  <div>
                    <p>{settings.business_hours}</p>
                    <span
                      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isOpen ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-600 border border-slate-200"
                      }`}
                      data-testid="footer-open-status"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                      {isOpen ? t("openNow") : t("closedNow")}
                    </span>
                  </div>
                </li>
              )}
              {(() => {
                const fee = Number(settings?.delivery_fee ?? 40);
                const thr = Number(settings?.free_delivery_threshold ?? 500);
                if (fee <= 0) {
                  return <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#2D5A27]" /> {t("codAvailable").replace(/./, "")}Free home delivery</li>;
                }
                if (thr > 0) {
                  return <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#2D5A27]" /> Free delivery above ₹{thr}</li>;
                }
                return <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#2D5A27]" /> Home delivery available</li>;
              })()}
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D5A27]" /> {t("codAvailable")}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm mb-3 text-[#1E293B]">{t("aboutUs")}</h4>
            <p className="text-sm text-slate-600 leading-relaxed">{t("trustedBy")}</p>
            {settings?.owner_name && (
              <p className="text-sm text-slate-700 mt-2 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-[#2D5A27]" />
                <span>{t("proprietor")}: <span className="font-semibold">{settings.owner_name}</span></span>
              </p>
            )}
            {settings?.shop_address && (
              <p className="text-xs text-slate-500 leading-relaxed mt-2 whitespace-pre-line">{settings.shop_address}</p>
            )}
            {settings?.maps_url && (
              <a
                href={settings.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-maps-link"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D5A27] hover:text-[#21421D]"
              >
                <Map className="w-3.5 h-3.5" /> {t("directions")}
              </a>
            )}
            {settings?.gst_number && (
              <p className="text-[10px] text-slate-500 mt-3 font-mono">GSTIN: {settings.gst_number}</p>
            )}
            {(settings?.fertilizer_license || settings?.seed_license || settings?.pesticide_license) && (
              <div className="mt-2 space-y-0.5" data-testid="footer-licenses">
                {settings?.fertilizer_license && <p className="text-[10px] text-slate-500 font-mono">Fert Lic: {settings.fertilizer_license}</p>}
                {settings?.seed_license && <p className="text-[10px] text-slate-500 font-mono">Seed Lic: {settings.seed_license}</p>}
                {settings?.pesticide_license && <p className="text-[10px] text-slate-500 font-mono">Pest Lic: {settings.pesticide_license}</p>}
              </div>
            )}
            <Link to="/admin/login" className="text-xs text-slate-400 hover:text-[#2D5A27] mt-4 inline-block" data-testid="footer-admin-link">
              {t("admin")}
            </Link>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-[#E2E8F0] text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {t("appName")}. {t("footerRights")}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
